// 批 3（审计 D-15 / D-16）：USDT 收付页的两个小动作，独立成模块，由 crypto.ts 的行操作钩子一行接入——
//   - 监听收入页签：地址监控 failed 时「恢复监控」（一人提议、另一人复核，禁止自审，按 monitoring_version 乐观锁）；
//   - 异常流水页签：入账后重组（reason=credited_reorg）待处理时「处理冻结」（释放冻结 / 确认损失建议，复核沿用异常详情抽屉）。
import type { RouteKey } from '../../../shared/src/api/routes';
import { ApiError } from '../../../shared/src/api/client';
import type { AdminCtx } from '../domains/types';
import { openDrawer, openForm, closeDrawer, reloadCurrent, sessionGuard } from '../domains/table';
import type { ActionDef, FormDef, Need } from './types';
import { localErr } from './ui';
import { facetText } from './crypto.monitor';

const EDIT: Need = { module: 'cryptoPayments', bit: 'edit' }, APPROVE: Need = { module: 'cryptoPayments', bit: 'approve' };

export const I18N_CRYPTO_MON = {
  zh: {
    cpmRestore: '恢复监控', cpmRestoreTitle: '恢复收款地址监控', cpmPropose: '提交恢复申请', cpmApprove: '复核通过并恢复', cpmReject: '驳回申请',
    cpmRestoreTip: '地址因链重组被标为监控失败，停止分配新订单。恢复须另一人复核：通过时服务器重新读取链头，从失败块起重新回扫，追平链头并经心跳确认后才重新可分配。',
    cpmNetwork: '网络', cpmAddress: '地址', cpmState: '监控状态', cpmReason: '失败原因', cpmFailedAt: '失败时间', cpmFailedBlock: '失败块', cpmVersion: '监控版本',
    cpmRequest: '待复核申请', cpmProposer: '提议人', cpmNote: '说明', cpmNoRequest: '暂无待复核申请', cpmNotFailed: '该地址当前不是监控失败状态，无需恢复',
    cpmPending: '已有待复核的恢复申请，请等待复核', cpmChanged: '地址监控状态已变化，请刷新后重新提交', cpmSelf: '不能复核自己提交的申请',
    cpmChain: '读链失败，暂不能恢复，请稍后重试', cpmNoteReq: '请填写说明（1～1000 字）', cpmSaved: '恢复申请已提交，等待另一人复核', cpmApproved: '已恢复，地址将在回扫追平后重新可分配', cpmRejected: '恢复申请已驳回',
    cpmHold: '处理冻结', cpmHoldTitle: '入账后重组：处理冻结余额',
    cpmHoldTip: '该笔已入账的充值在终局复核窗口内被确认链重组，系统已冻结对应余额（用户已花掉的部分记为差额）。请核实后提交建议，由另一人在异常详情中复核；不会自动冲账。',
    cpmHoldAction: '处理方式', cpmRelease: '释放冻结（款项有效 / 平台承担）', cpmConfirmLoss: '确认损失（冻结额转为扣减）', cpmCredited: '入账净额', cpmFrozen: '已冻结', cpmShortfall: '未能冻结差额',
    cpmReappeared: '冻结后交易重新出现', cpmEvidenceKind: '证据类型', cpmEvidence: '证据引用', cpmEvidencePh: 'review-record:记录编号',
    cpmHoldInvalid: '请选择处理方式并填写有效的证据引用与说明', cpmHoldSaved: '建议已提交，请另一人在异常详情中复核', cpmHoldChanged: '异常或冻结状态已变化，请刷新后重新提交', cpmYes: '是', cpmNo: '否',
    cpmAck: '确认知悉', cpmAckTitle: '已入账充值核验异常：确认知悉',
    cpmAckTip: '该笔充值已过终局窗口并入账，复核时链上证据发生变化，系统只登记异常并告警，没有冻结余额。核查后提交「确认知悉」建议，由另一人在异常详情中复核；结案不改动入账与余额。',
    cpmAckInvalid: '请填写有效的证据引用与说明', cpmAckSaved: '确认知悉建议已提交，请另一人在异常详情中复核',
  },
  en: {
    cpmRestore: 'Restore monitoring', cpmRestoreTitle: 'Restore address monitoring', cpmPropose: 'Request restore', cpmApprove: 'Approve and restore', cpmReject: 'Reject request',
    cpmRestoreTip: 'Monitoring failed after a confirmed chain reorg, so the address takes no new orders. A second operator must approve: the server re-reads the chain head, rescans from the failure block, and the address becomes allocatable only after the rescan catches up and the heartbeat confirms it.',
    cpmNetwork: 'Network', cpmAddress: 'Address', cpmState: 'Monitor state', cpmReason: 'Failure reason', cpmFailedAt: 'Failed at', cpmFailedBlock: 'Failure block', cpmVersion: 'Monitor version',
    cpmRequest: 'Pending request', cpmProposer: 'Proposed by', cpmNote: 'Note', cpmNoRequest: 'No pending request', cpmNotFailed: 'This address is not in the failed state',
    cpmPending: 'A restore request is already pending review', cpmChanged: 'Monitoring state changed. Refresh and submit again.', cpmSelf: 'You cannot review your own request',
    cpmChain: 'Chain read failed. Try again later.', cpmNoteReq: 'Enter a note (1-1000 characters)', cpmSaved: 'Restore request submitted for independent review', cpmApproved: 'Restored. The address becomes allocatable after the rescan catches up.', cpmRejected: 'Restore request rejected',
    cpmHold: 'Handle frozen funds', cpmHoldTitle: 'Post-credit reorg: frozen balance',
    cpmHoldTip: 'This credited deposit was confirmed reorged within the finality window. The matching balance is frozen (any amount already spent is recorded as a shortfall). Submit a proposal; a second operator approves it from the exception detail. Nothing is reversed automatically.',
    cpmHoldAction: 'Action', cpmRelease: 'Release hold (funds valid / platform absorbs)', cpmConfirmLoss: 'Confirm loss (hold becomes a deduction)', cpmCredited: 'Credited net', cpmFrozen: 'Frozen', cpmShortfall: 'Shortfall not frozen',
    cpmReappeared: 'Transaction reappeared after hold', cpmEvidenceKind: 'Evidence type', cpmEvidence: 'Evidence references', cpmEvidencePh: 'review-record:record_id',
    cpmHoldInvalid: 'Choose an action and enter valid evidence references and a note', cpmHoldSaved: 'Proposal submitted. A second operator reviews it in the exception detail.', cpmHoldChanged: 'The exception or hold changed. Refresh and submit again.', cpmYes: 'Yes', cpmNo: 'No',
    cpmAck: 'Acknowledge', cpmAckTitle: 'Credited deposit verification exception: acknowledge',
    cpmAckTip: 'This deposit was credited after the finality window. Its on-chain evidence changed during re-verification, so the system recorded an exception and raised an alert without freezing any balance. After checking, submit an acknowledgement; a second operator approves it from the exception detail. Closing it does not change the credit or the balance.',
    cpmAckInvalid: 'Enter valid evidence references and a note', cpmAckSaved: 'Acknowledgement submitted. A second operator reviews it in the exception detail.',
  },
} as const;

type MonKey = keyof (typeof I18N_CRYPTO_MON)['zh'];
const mt = (c: AdminCtx, k: MonKey): string => I18N_CRYPTO_MON[c.lang === 'en' ? 'en' : 'zh'][k];

/** 页面行中本模块用到的字段（异常行 / 地址行）。 */
export type MonRow = { id: number; status: string; version: number; monitoring_state?: string; reason?: string };

/** GET /api/admin/crypto-addresses/{id}/monitoring-restore 的待复核申请。 */
export type RestoreReq = { id: number; status: string; version: number; proposed_by: number; proposed_account?: string; note: string; monitoring_version: number; failed_reason: string };
/** GET /api/admin/crypto-addresses/{id}/monitoring-restore。 */
export type RestoreStatus = {
  registry_id: number; network: string; address: string; monitoring_state: string; monitoring_version: number;
  monitoring_failed_at: string | null; monitoring_failed_reason: string | null; monitoring_failed_block: number | null; request: RestoreReq | null;
};
/** GET /api/admin/crypto-exceptions/{id}/reorg-hold。 */
export type ReorgHold = { id: number; exception_id: number; deposit_id: number; user_id: number; credited_amount: string; frozen_amount: string; shortfall_amount: string; status: string; reappeared_at: string | null };

const noteOK = (v: unknown): boolean => { const s = String(v ?? '').trim(); return s.length > 0 && s.length <= 1000; };
const refsOK = (v: string): boolean => { const xs = v.split(',').map(x => x.trim()); return xs.length >= 1 && xs.length <= 8 && xs.every(x => /^(wallet-proof|exchange-receipt|review-record):[A-Za-z0-9_-]{8,128}$/.test(x)); };

/** 后端拒绝原因 → 本模块文案；其他错误原样抛出交给外壳统一提示。 */
export function monitoringErr(c: AdminCtx, route: RouteKey, e: unknown): unknown {
  if (!(e instanceof ApiError)) return e;
  const reason = e.data.reason;
  if (e.code === 1012 && reason === 'restore_pending') return localErr(route, mt(c, 'cpmPending'));
  if (e.code === 1012 && reason === 'monitoring_not_failed') return localErr(route, mt(c, 'cpmNotFailed'));
  if (e.code === 1012 && reason === 'independent_reviewer_required') return localErr(route, mt(c, 'cpmSelf'));
  if (e.code === 1012 && reason === 'crypto_evidence_or_state_changed') return localErr(route, mt(c, route.includes('crypto-exceptions') ? 'cpmHoldChanged' : 'cpmChanged'));
  if (e.code === 1403) return localErr(route, mt(c, 'cpmChain'));
  if (e.code === 4220 && e.data.field === 'note') return localErr(route, mt(c, 'cpmNoteReq'));
  return e;
}

function statusFields(c: AdminCtx): FormDef['fields'] {
  return [
    { k: 'network', label: mt(c, 'cpmNetwork'), type: 'readonly' }, { k: 'address', label: mt(c, 'cpmAddress'), type: 'readonly', wide: true },
    { k: 'reason', label: mt(c, 'cpmReason'), type: 'readonly' }, { k: 'block', label: mt(c, 'cpmFailedBlock'), type: 'readonly' },
  ];
}
function statusInit(st: RestoreStatus): Record<string, string> {
  return { network: st.network, address: st.address, reason: st.monitoring_failed_reason ?? '—', block: st.monitoring_failed_block == null ? '—' : String(st.monitoring_failed_block) };
}

/** 提议恢复：绑定页面读到的 monitoring_version。 */
export function restoreProposeForm(c: AdminCtx, st: RestoreStatus): FormDef {
  return {
    title: mt(c, 'cpmRestoreTitle'), tip: mt(c, 'cpmRestoreTip'),
    fields: [...statusFields(c), { k: 'note', label: mt(c, 'cpmNote'), type: 'text', wide: true }],
    init: { ...statusInit(st), note: '' },
    validate: (v) => (noteOK(v['note']) ? '' : mt(c, 'cpmNoteReq')),
    submit: async (ctx, v) => {
      const alive = sessionGuard(ctx);
      const route: RouteKey = 'POST /api/admin/crypto-addresses/{id}/monitoring-restore';
      try {
        await ctx.gp.api.call(route, { path: { id: st.registry_id }, body: { version: st.monitoring_version, note: String(v['note']).trim() } });
      } catch (e) { throw monitoringErr(ctx, route, e); }
      if (!alive()) return;
      ctx.gp.ui.toast(mt(ctx, 'cpmSaved')); closeDrawer(ctx); await reloadCurrent(ctx);
    },
  };
}

/** 复核恢复申请：禁止自审（服务端同样校验）；驳回须填写说明。 */
export function restoreReviewForm(c: AdminCtx, st: RestoreStatus, req: RestoreReq, approve: boolean): FormDef {
  const own = (x: AdminCtx): boolean => !!req.proposed_account && req.proposed_account === x.s.sh.me?.account;
  return {
    title: mt(c, approve ? 'cpmApprove' : 'cpmReject'), tip: mt(c, 'cpmRestoreTip'),
    fields: [...statusFields(c), { k: 'proposer', label: mt(c, 'cpmProposer'), type: 'readonly' }, { k: 'proposal', label: mt(c, 'cpmRequest'), type: 'readonly', wide: true },
      { k: 'note', label: mt(c, 'cpmNote'), type: 'text', wide: true, optional: approve }],
    init: { ...statusInit(st), proposer: req.proposed_account ?? String(req.proposed_by), proposal: req.note, note: '' },
    validate: (v) => (own(c) ? mt(c, 'cpmSelf') : (approve ? String(v['note'] ?? '').length <= 1000 : noteOK(v['note'])) ? '' : mt(c, 'cpmNoteReq')),
    submit: async (ctx, v) => {
      if (own(ctx)) throw Error(mt(ctx, 'cpmSelf'));
      const alive = sessionGuard(ctx);
      const route: RouteKey = approve ? 'POST /api/admin/crypto-monitoring-restores/{id}/approve' : 'POST /api/admin/crypto-monitoring-restores/{id}/reject';
      try {
        await ctx.gp.api.call(route, { path: { id: req.id }, body: { version: req.version, note: String(v['note'] ?? '').trim() } });
      } catch (e) { throw monitoringErr(ctx, route, e); }
      if (!alive()) return;
      ctx.gp.ui.toast(mt(ctx, approve ? 'cpmApproved' : 'cpmRejected')); closeDrawer(ctx); await reloadCurrent(ctx);
    },
  };
}

/** 打开恢复监控抽屉：先取失败信息与待复核申请，再按状态给出「提议」或「复核通过 / 驳回」。 */
export async function openRestore(c: AdminCtx, r: MonRow): Promise<void> {
  const alive = sessionGuard(c);
  const st = await c.gp.api.call<RestoreStatus>('GET /api/admin/crypto-addresses/{id}/monitoring-restore', { path: { id: r.id } });
  if (!alive()) return;
  const req = st.request;
  const own = !!req?.proposed_account && req.proposed_account === c.s.sh.me?.account;
  openDrawer(c, {
    kind: 'crypto-monitoring', title: `${mt(c, 'cpmRestoreTitle')} #${st.registry_id}`,
    fields: [{ k: mt(c, 'cpmNetwork'), v: st.network }, { k: mt(c, 'cpmAddress'), v: st.address, wide: true }, { k: mt(c, 'cpmState'), v: st.monitoring_state },
      { k: mt(c, 'cpmReason'), v: st.monitoring_failed_reason ?? '—' }, { k: mt(c, 'cpmFailedAt'), v: st.monitoring_failed_at ?? '—' },
      { k: mt(c, 'cpmFailedBlock'), v: st.monitoring_failed_block == null ? '—' : String(st.monitoring_failed_block) }, { k: mt(c, 'cpmVersion'), v: String(st.monitoring_version) },
      { k: mt(c, 'cpmRequest'), v: req ? `#${req.id} · ${req.proposed_account ?? req.proposed_by} · ${req.note}` : mt(c, 'cpmNoRequest'), wide: true }],
    json: JSON.stringify(st, null, 2),
    actions: [
      ...(!req && st.monitoring_state === 'failed' ? [{ label: mt(c, 'cpmPropose'), kind: 'primary' as const, need: EDIT, run: (ctx: AdminCtx) => openForm(ctx, restoreProposeForm(ctx, st)) }] : []),
      ...(req ? [{ label: mt(c, 'cpmApprove'), kind: 'primary' as const, need: APPROVE, gate: () => ({ disabled: own, title: own ? mt(c, 'cpmSelf') : '' }), run: (ctx: AdminCtx) => openForm(ctx, restoreReviewForm(ctx, st, req, true)) },
        { label: mt(c, 'cpmReject'), kind: 'danger' as const, need: APPROVE, gate: () => ({ disabled: own, title: own ? mt(c, 'cpmSelf') : '' }), run: (ctx: AdminCtx) => openForm(ctx, restoreReviewForm(ctx, st, req, false)) }] : []),
    ],
  });
}

/** 入账后重组处理建议：只有释放冻结 / 确认损失两种动作，不带用户、订单或退款目标。 */
export function reorgHoldForm(c: AdminCtx, r: MonRow, hold: ReorgHold): FormDef {
  const label = (v: string): string => (v === 'release_hold' ? mt(c, 'cpmRelease') : v === 'confirm_loss' ? mt(c, 'cpmConfirmLoss') : v);
  return {
    title: mt(c, 'cpmHoldTitle'), tip: mt(c, 'cpmHoldTip'),
    fields: [{ k: 'credited', label: mt(c, 'cpmCredited'), type: 'readonly' }, { k: 'frozen', label: mt(c, 'cpmFrozen'), type: 'readonly' }, { k: 'shortfall', label: mt(c, 'cpmShortfall'), type: 'readonly' },
      { k: 'reappeared', label: mt(c, 'cpmReappeared'), type: 'readonly' },
      { k: 'action', label: mt(c, 'cpmHoldAction'), type: 'select', options: ['release_hold', 'confirm_loss'], optionLabel: (_t, v) => label(v) },
      { k: 'evidence_kind', label: mt(c, 'cpmEvidenceKind'), type: 'select', options: ['verified_record', 'wallet_signature', 'exchange_receipt'], optionLabel: facetText('evidenceKind') },
      { k: 'evidence_refs', label: mt(c, 'cpmEvidence'), type: 'text', wide: true, placeholder: mt(c, 'cpmEvidencePh') },
      { k: 'note', label: mt(c, 'cpmNote'), type: 'text', wide: true }],
    init: { credited: hold.credited_amount, frozen: hold.frozen_amount, shortfall: hold.shortfall_amount, reappeared: hold.reappeared_at ? mt(c, 'cpmYes') : mt(c, 'cpmNo'),
      action: 'release_hold', evidence_kind: 'verified_record', evidence_refs: '', note: '' },
    validate: (v) => ((v['action'] === 'release_hold' || v['action'] === 'confirm_loss') && refsOK(String(v['evidence_refs'] ?? '')) && noteOK(v['note']) ? '' : mt(c, 'cpmHoldInvalid')),
    submit: async (ctx, v) => {
      const alive = sessionGuard(ctx);
      const route: RouteKey = 'POST /api/admin/crypto-exceptions/{id}/actions';
      try {
        await ctx.gp.api.call(route, { path: { id: r.id }, body: { action: String(v['action']), version: r.version,
          evidence_refs: { kind: String(v['evidence_kind']), refs: String(v['evidence_refs']).split(',').map(x => x.trim()) }, note: String(v['note']).trim() } });
      } catch (e) { throw monitoringErr(ctx, route, e); }
      if (!alive()) return;
      ctx.gp.ui.toast(mt(ctx, 'cpmHoldSaved')); closeDrawer(ctx); await reloadCurrent(ctx);
    },
  };
}

/** 合并遗留 M1：已终局入账事件的 unverified 异常「确认知悉」建议（action=acknowledge，不带用户、订单或退款目标；复核沿用异常详情抽屉）。 */
export function acknowledgeForm(c: AdminCtx, r: MonRow): FormDef {
  return {
    title: mt(c, 'cpmAckTitle'), tip: mt(c, 'cpmAckTip'),
    fields: [{ k: 'evidence_kind', label: mt(c, 'cpmEvidenceKind'), type: 'select', options: ['verified_record', 'wallet_signature', 'exchange_receipt'], optionLabel: facetText('evidenceKind') },
      { k: 'evidence_refs', label: mt(c, 'cpmEvidence'), type: 'text', wide: true, placeholder: mt(c, 'cpmEvidencePh') },
      { k: 'note', label: mt(c, 'cpmNote'), type: 'text', wide: true }],
    init: { evidence_kind: 'verified_record', evidence_refs: '', note: '' },
    validate: (v) => (refsOK(String(v['evidence_refs'] ?? '')) && noteOK(v['note']) ? '' : mt(c, 'cpmAckInvalid')),
    submit: async (ctx, v) => {
      const alive = sessionGuard(ctx);
      const route: RouteKey = 'POST /api/admin/crypto-exceptions/{id}/actions';
      try {
        await ctx.gp.api.call(route, { path: { id: r.id }, body: { action: 'acknowledge', version: r.version,
          evidence_refs: { kind: String(v['evidence_kind']), refs: String(v['evidence_refs']).split(',').map(x => x.trim()) }, note: String(v['note']).trim() } });
      } catch (e) { throw monitoringErr(ctx, route, e); }
      if (!alive()) return;
      ctx.gp.ui.toast(mt(ctx, 'cpmAckSaved')); closeDrawer(ctx); await reloadCurrent(ctx);
    },
  };
}

/** 先取冻结额与差额再开处理表单。 */
export async function openReorgHold(c: AdminCtx, r: MonRow): Promise<void> {
  const alive = sessionGuard(c);
  const hold = await c.gp.api.call<ReorgHold>('GET /api/admin/crypto-exceptions/{id}/reorg-hold', { path: { id: r.id } });
  if (!alive()) return;
  openForm(c, reorgHoldForm(c, r, hold));
}

/**
 * crypto.ts 行操作钩子：tab = 页签序号（0 异常流水、1 监听收入）。
 * 监听收入行 monitoring_state=failed → 「恢复监控」；异常行 reason=credited_reorg 且 open → 「处理冻结」；
 * 异常行 reason=unverified 且 open → 「确认知悉」（合并遗留 M1）。
 */
export function cryptoMonitoringActions<R extends MonRow>(c: AdminCtx, tab: number, r: R): ActionDef<R>[] {
  // 抽屉本身只读（view）；提议按钮要 edit、复核按钮要 approve，由抽屉动作分别控权。
  if (tab === 1 && r.monitoring_state === 'failed') return [{ label: '~' + mt(c, 'cpmRestore'), run: (ctx) => openRestore(ctx, r) }];
  if (tab === 0 && r.reason === 'credited_reorg' && r.status === 'open') return [{ label: '~' + mt(c, 'cpmHold'), need: EDIT, run: (ctx) => openReorgHold(ctx, r) }];
  if (tab === 0 && r.reason === 'unverified' && r.status === 'open') return [{ label: '~' + mt(c, 'cpmAck'), need: EDIT, run: (ctx) => openForm(ctx, acknowledgeForm(ctx, r)) }];
  return [];
}
